import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { Button } from './button';

interface TimePickerProps {
  value: string; // HH:MM 格式
  onChange: (value: string) => void;
  placeholder?: string;
}

const generateTimeOptions = () => {
  const times = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 5) {
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      times.push(timeStr);
    }
  }
  return times;
};

export const TimePicker = forwardRef<{ focus: () => void }, TimePickerProps>(
  ({ value, onChange, placeholder = '選擇時間' }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const [popupStyle, setPopupStyle] = useState({});

    const timeOptions = generateTimeOptions();

    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      }
    }));

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && popupRef.current &&
            !containerRef.current.contains(event.target as Node) &&
            !popupRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen]);

    const updatePopupPosition = () => {
      if (containerRef.current && popupRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const popupHeight = popupRef.current.offsetHeight;

        // 計算水平位置，確保不超出屏幕右側
        let left = rect.left;
        if (left + rect.width > window.innerWidth) {
          left = window.innerWidth - rect.width - 16;
        }
        if (left < 16) {
          left = 16;
        }

        // 計算垂直位置，確保不超出屏幕底部
        let top = rect.bottom + 8;
        if (top + popupHeight > window.innerHeight) {
          top = rect.top - popupHeight - 8;
        }

        setPopupStyle({
          position: 'fixed',
          top: `${top}px`,
          left: `${left}px`,
          width: `${rect.width}px`,
          zIndex: 9999,
        });
      }
    };

    useEffect(() => {
      if (isOpen) {
        updatePopupPosition();
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
        const handleResize = () => updatePopupPosition();
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize, true);
        return () => {
          window.removeEventListener('resize', handleResize);
          window.removeEventListener('scroll', handleResize, true);
        };
      }
    }, [isOpen]);

    const handleTimeSelect = (time: string) => {
      onChange(time);
      setIsOpen(false);
      setSearchInput('');
    };

    const normalizeSearchInput = (input: string) => {
      return input.replace(/:/g, '');
    };

    const normalizeTime = (time: string) => {
      return time.replace(/:/g, '');
    };

    const normalizedSearchInput = normalizeSearchInput(searchInput);

    const filteredTimes = timeOptions.filter((time) => {
      if (!normalizedSearchInput) return true;
      const normalizedTime = normalizeTime(time);
      return (
        normalizedTime.includes(normalizedSearchInput) ||
        time.includes(searchInput)
      );
    });

    const popupContent = (
      <div ref={popupRef} style={popupStyle as React.CSSProperties} className="bg-white border border-gray-300 rounded-lg shadow-lg max-w-xs">
        <div className="p-2 border-b sticky top-0 bg-white z-10">
          <input
            ref={inputRef}
            type="text"
            placeholder="搜尋時間 (例: 102 或 10:2)"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full h-8 px-2 py-1 text-sm border border-gray-300 rounded"
            autoComplete="off"
            onClick={(e) => e.stopPropagation()}
          />
        </div>

        <div className="max-h-48 overflow-y-auto">
          {filteredTimes.length > 0 ? (
            filteredTimes.map((time) => (
              <button
                key={time}
                type="button"
                onClick={() => handleTimeSelect(time)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${
                  value === time ? 'bg-blue-100 text-blue-700 font-medium' : ''
                }`}
              >
                {time}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">找不到符合的時間</div>
          )}
        </div>

        <div className="border-t p-2 flex justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setIsOpen(false)}
            className="text-xs h-8"
          >
            關閉
          </Button>
        </div>
      </div>
    );

    return (
      <div ref={containerRef} className="relative w-full">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full h-10 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between"
        >
          <span className={value ? 'text-gray-900' : 'text-gray-500'}>
            {value || placeholder}
          </span>
          <ChevronDown size={16} className="text-gray-400" />
        </button>
        {isOpen && createPortal(popupContent, document.body)}
      </div>
    );
  }
);
